import { useQuery } from "@tanstack/react-query";
import { Outlet, createFileRoute, redirect, useMatches } from "@tanstack/react-router";

import { containerQueries } from "@/features/containers";
import { IncognitoSupportBanner } from "@/features/superuser/incognito/components/incognito-support-banner";
import {
  INCOGNITO_SEARCH_VALUE,
  parseIncognitoSearch,
} from "@/features/superuser/incognito/incognito-route";
import { loadIncognitoWorkspace } from "@/features/superuser/incognito/incognito-route-guard";
import { isContributorWorkspacePathAllowed } from "@/features/workspaces/contributor-route";
import { MEMBER_ACCESS, workspaceAccessFromRole } from "@/features/workspaces/workspace-access";
import type { WorkspaceAccess } from "@/features/workspaces/workspace-access";
import { ACTIVE_WORKSPACE_SLUG_KEY } from "@/lib/local-storage.consts";
import type { Workspace } from "@shared/types/supabase.types";

export const Route = createFileRoute("/_authenticated/workspaces/$slug")({
  component: WorkspaceLayout,

  // CR-116: `?incognito=support` selects the superuser read-only load path.
  validateSearch: parseIncognitoSearch,

  beforeLoad: async ({
    params: { slug },
    context,
    search,
    location,
  }): Promise<{ workspace: Workspace; workspaceAccess: WorkspaceAccess }> => {
    if (search.incognito === INCOGNITO_SEARCH_VALUE) {
      return loadIncognitoWorkspace({
        slug,
        pathname: location.pathname,
        email: context.user.email,
        queryClient: context.queryClient,
      });
    }

    let workspace: Workspace;
    try {
      workspace = await context.queryClient.ensureQueryData(containerQueries.workspaceBySlug(slug));
    } catch (error) {
      localStorage.removeItem(ACTIVE_WORKSPACE_SLUG_KEY);
      throw redirect({ to: "/workspaces" });
    }

    // CR-183: a caller who only holds the contributor anchor row gets the
    // contributor mode: the workspace home and their projects' suggestions
    // page, every other workspace path lands on the home. A failed read keeps
    // the member path (RLS still refuses a contributor everything else).
    let workspaceAccess: WorkspaceAccess = MEMBER_ACCESS;
    try {
      workspaceAccess = workspaceAccessFromRole(
        await context.queryClient.ensureQueryData({
          ...containerQueries.myWorkspaceAccess(context.user.id, workspace.id),
          revalidateIfStale: true,
        }),
      );
    } catch {
      workspaceAccess = MEMBER_ACCESS;
    }

    if (
      workspaceAccess.mode === "contributor" &&
      !isContributorWorkspacePathAllowed(location.pathname, slug)
    ) {
      throw redirect({ to: "/workspaces/$slug", params: { slug }, replace: true });
    }

    return { workspace, workspaceAccess };
  },
});

export const WorkspaceRoute = Route;

const PROJECT_ROUTE_ID = "/_authenticated/workspaces/$slug/projects/$projectSlug";

function WorkspaceLayout() {
  const { workspace, workspaceAccess } = Route.useRouteContext();
  // CR-165: a project page is a full-height sidebar layout, so it renders the
  // banner inside its own content pane instead.
  const inProject = useMatches({
    select: (matches) => matches.some((match) => match.routeId === PROJECT_ROUTE_ID),
  });

  if (workspaceAccess.mode === "incognito") {
    return (
      <>
        {inProject ? null : (
          <IncognitoSupportBanner workspaceName={workspace.name ?? workspace.slug ?? ""} />
        )}
        <Outlet />
      </>
    );
  }

  return <MemberWorkspaceLayout />;
}

function MemberWorkspaceLayout() {
  const { slug } = Route.useParams();

  // Keep workspace fresh (cached from beforeLoad)
  useQuery({
    ...containerQueries.workspaceBySlug(slug),
    staleTime: 60 * 60 * 1000, // 1h
    refetchOnWindowFocus: false,
  });

  return <Outlet />;
}
